import { useEffect, useState, useContext, useRef, useMemo } from "react";
import MenuItem from "@mui/material/MenuItem";
import { StyledText, StyledBox } from "./SelectedCustomerDropDown.style";
import { useNavigate } from "react-router-dom";
import { useHomePageHooks } from "pages/Home/Home.hook";
import UserContext from "common/Context/UserContext";
import Clock from "../Header/Components/Clock";
import { Dropdown } from "../DropdownComponent/Dropdown";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import {
  isPlatformAdminProfile,
  platformAdminUsesCustomerContextFromMe,
} from "common/Utilities/Role.utils";
import {
  filterSelectableCustomerAccessRows,
  isSelectableCustomerAccessRow,
} from "common/Utilities/CustomerSelection.utils";
import { applyNavigationAfterCustomerSwitch } from "common/Utilities/EffectiveAccess.utils";

const pickDefaultCustomerRow = (customerList, profile) => {
  const selectable = filterSelectableCustomerAccessRows(customerList);
  if (!selectable.length) return null;
  const id = profile?.activeCustomerId;
  const code =
    profile?.activeCustomerCode;
  if (id != null && String(id).trim() !== "") {
    const row = selectable.find((c) => String(c.id) === String(id));
    return row ?? null;
  }
  if (code != null && String(code).trim() !== "") {
    const row = selectable.find(
      (c) =>
        String(c.customerCode) === String(code)
    );
    return row ?? null;
  }
  return selectable[0] ?? null;
};

const resolveSelectableCustomer = (customer, selectableList) => {
  if (!customer || !selectableList?.length) {
    return null;
  }
  const code = customer.customerCode;
  return (
    selectableList.find(
      (c) =>
        String(c.id) === String(customer.id) ||
        String(c.customerCode) === String(code)
    ) ?? null
  );
};

const resolveCustomerRow = (customerList, customerInfo) => {
  if (!customerInfo || !customerList?.length) {
    return customerInfo;
  }
  return (
    customerList.find(
      (c) =>
        String(c.id) === String(customerInfo.id) ||
        String(c.customerCode) === String(customerInfo.customerCode)
    ) ?? customerInfo
  );
};

export default function SelectedCustomerDropdown() {
  const navigate = useNavigate();
  const {
    customerList,
    userProfile,
    headerSelectedCustomer,
    setHeaderSelectedCustomer,
    switchActiveCustomer,
  } = useContext(UserContext);
  const { actions } = useHomePageHooks();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isSwitchingCustomer, setIsSwitchingCustomer] = useState(false);
  const previousCustomerRef = useRef(null);
  const { noCustomerSelected, viewing } = INFRAMARK_CONSTANTS.selectedCustomer;
  const borderColor = "white";
  const profile = userProfile?.profile;
  const selectableCustomerList = useMemo(
    () => filterSelectableCustomerAccessRows(customerList ?? []),
    [customerList]
  );
  const customerDropdownOptions = useMemo(
    () =>
      selectableCustomerList.map((customer) => ({
        value: customer?.id,
        label: customer?.name,
      })),
    [selectableCustomerList]
  );

  useEffect(() => {
    if (!customerList?.length) {
      setSelectedCustomer(null);
      return;
    }
    if (isPlatformAdminProfile(profile)) {
      const nextSelection = platformAdminUsesCustomerContextFromMe(profile)
        ? resolveSelectableCustomer(
            headerSelectedCustomer,
            selectableCustomerList
          )
        : null;
      setSelectedCustomer(nextSelection);
      return;
    }
    const candidate =
      headerSelectedCustomer ?? pickDefaultCustomerRow(customerList, profile);
    setSelectedCustomer(
      resolveSelectableCustomer(candidate, selectableCustomerList)
    );
  }, [customerList, profile, headerSelectedCustomer, selectableCustomerList]);

  const handleChange = async (event) => {
    const selectedId = event.target.value;
    const selectedCustomerObj = selectableCustomerList.find(
      (c) => String(c.id) === String(selectedId)
    );
    if (
      !selectedCustomerObj ||
      !isSelectableCustomerAccessRow(selectedCustomerObj) ||
      isSwitchingCustomer
    ) {
      return;
    }

    previousCustomerRef.current = selectedCustomer;
    setIsSwitchingCustomer(true);
    setHeaderSelectedCustomer(selectedCustomerObj);
    setSelectedCustomer(selectedCustomerObj);

    try {
      if (profile?.sessionFromMe) {
        const result = await switchActiveCustomer(selectedCustomerObj);
        if (result?.error) {
          const previous = previousCustomerRef.current;
          setSelectedCustomer(previous);
          setHeaderSelectedCustomer(previous);
          return;
        }

        const syncedCustomer = resolveCustomerRow(
          customerList,
          result?.customerInfo ?? selectedCustomerObj
        );
        actions.setSlectedCustomer(syncedCustomer, profile?.userid);
        setHeaderSelectedCustomer(syncedCustomer);
        setSelectedCustomer(syncedCustomer);

        window.dispatchEvent(
          new CustomEvent("customerChanged", { detail: syncedCustomer })
        );
        applyNavigationAfterCustomerSwitch(profile, navigate);
      } else {
        actions.setSlectedCustomer(selectedCustomerObj, profile?.userid);
        window.dispatchEvent(
          new CustomEvent("customerChanged", { detail: selectedCustomerObj })
        );
        navigate(INFRAMARK_CONSTANTS?.navigationRoutes?.administration);
      }
    } finally {
      setIsSwitchingCustomer(false);
    }
  };

  return (
    <>
      {!(customerDropdownOptions.length === 1 && selectedCustomer) ? (
        <StyledBox>
          <StyledText>{viewing} </StyledText>
          <Dropdown
            displayValue={selectedCustomer?.name || noCustomerSelected}
            value={selectedCustomer?.id ?? ""}
            handleChange={handleChange}
            borderColor={borderColor}
            searchable={true}
            options={customerDropdownOptions}
          >
            {customerDropdownOptions.map((customer) => (
              <MenuItem key={customer?.value} value={customer?.value}>
                {customer?.label}
              </MenuItem>
            ))}
          </Dropdown>
        </StyledBox>
      ) : selectedCustomer ? (
        <StyledText>
          {viewing} {selectedCustomer?.name}
        </StyledText>
      ) : null}
      {selectedCustomer?.timeZone ? (
        <Clock timezone={selectedCustomer?.timeZone} />
      ) : null}
    </>
  );
}